import { Spinner } from "construct-ui";
import SingleSelectComponent from "../../../components/single-select/single-select.component";
import userSearchModal from "../../../models/common/user-search.modal";
import languageModel from "../../../models/language.model";
import UserRoleMenuMapModel from "../../../models/user-role-menu-map/user-role-menu-map.model";
import ViewBase from "../../view-base.view";
import MenuMapTableComponent from "./menu-map-table.component";

/**
 * User Role Menu Map View
 * 
 * A page that manage menu of user roles
 * 
 * @author cmcnkhiep(Ryan)
 */

export default function UserRoleMenuMapView() {
    ViewBase.call(this);

    const SYSTEM_TYPE = { WEB: 0, MOBILE: 1 };

    let userRoleOptionList = [];
    let selectedUserRole = {};
    let systemType = 0;
    let userRoleMenu = [];
    let userRoleMenuMapHasChanged = false;
    let isTableLoading = true;

    let getUserRoleList = () => {
        userSearchModal.getUserRoleList(
            (result) => {
                userRoleOptionList = result;
                selectedUserRole = result[0];
                getUserRoleMenuMap();
            },
            (error) => {
                this.handleError(error);
            }
        )
    }

    // MARK: Rules:
    // inactive(0), active(1), indeterminate(2).
    // inactive(0), indeterminate(2) -> active(1).
    // active(1) -> inactive(0).

    const makeNode = (data, activation, parent, children) => {
        let node = {
            ...data,
            activation: activation,
            parent: parent,
            children: children,
            addChild(child) {
                child.parent = node;
                node.children.push(child);
            },
            toggle() {
                // inactive(0), indeterminate(2) -> active(1).
                // active(1) -> inactive(0).
                let activated = node.activation === 1 ? 0 : 1;
                node._setDownward(activated);
                node.parent?._setUpward(activated);
            },
            _setDownward(activated) {
                node.activation = activated;
                node.children.forEach(e => e._setDownward(activated));
            },
            _setUpward(activated) {
                if (activated === 2) {
                    // indeterminate(2).
                    node.activation = activated;
                    if (node.parent?.activation === 2) return; // don't need to go upward, reduce operation.
                    node.parent?._setUpward(activated); // set upward straight, reduce operation.
                } else {
                    // inactive(0), active(1).
                    let indeterminate = node.children.some(e => e.activation != activated);
                    if (indeterminate) {
                        node._setUpward(2);
                    } else {
                        node.activation = activated; // inactive(0), active(1).
                        node.parent?._setUpward(activated); // go upward and check indeterminate(2) again.
                    }
                }
            },
        };
        return node;
    };

    // [active] from API is true/false.
    const parseActivation = (item, children) => (item.active ? 1 : 0);

    // [parentMenuId] and [menuId] from API is string.
    const isParent = (item, parent) => (item.parentMenuId == (parent?.menuId ?? ''));

    const convertArrayToTree = (items, parseActivation, isParent, parent = null) => {
        return items.filter(item => isParent(item, parent)).map(item => {
            let node = makeNode(item, 0, parent, []);
            let children = convertArrayToTree(items, parseActivation, isParent, node);
            node.children = children;
            node.activation = parseActivation(node, children);
            return node;
        });
    };

    const getUserRoleMenuMap = () => {
        isTableLoading = true;
        UserRoleMenuMapModel.getUserRoleMenuByCountry(selectedUserRole.code, systemType).then((result) => {
            if (result.length) {
                userRoleMenu = convertArrayToTree(result, parseActivation, isParent, null);
            }
        }).catch((error) => { this.handleError(error) }).finally(() => { isTableLoading = false; })
    }

    const flattenTreeNode = (tree) =>
        tree.flatMap((node) => {
            const active = node.activation > 0 //Because it depend on changes on the UI 
            return [{ active, menuId: node.menuId }, ...flattenTreeNode(node.children)]
        })

    const saveUserRoleMenuByCountry = (e) => {
        e.preventDefault();

        if (!userRoleMenuMapHasChanged || !selectedUserRole.code) {
            return;
        }

        this.showLoading();

        let menuStatusList = flattenTreeNode(userRoleMenu);

        UserRoleMenuMapModel.saveUserRoleMenuByCountry({
            systemType,
            userRoleCode: selectedUserRole.code,
            menuStatusList
        }).then(() => {
            this.showMessage(
                this.getText("EM00000762", "Changes have been saved."),
                this.getText("EM0000004", "Success"),
                getUserRoleMenuMap
            );
        }).catch((error) => {
            this.handleError(error)
        }).finally(() => {
            this.hideLoading();
        })
    }

    const leavePageEventListener = (e) => {
        if (userRoleMenuMapHasChanged && !confirm(this.getText('EM00000776', 'Changes you made may not be saved'))) {
            e.preventDefault();
            e.stopImmediatePropagation();
        }
    };

    return {
        oncreate: () => {
            addEventListener('route', leavePageEventListener);
            addEventListener('beforeunload', leavePageEventListener);
        },
        onremove: () => {
            removeEventListener('route', leavePageEventListener);
            removeEventListener('beforeunload', leavePageEventListener);
        },
        oninit: () => {
            getUserRoleList();
        },
        view: () => {
            return [
                m('.card.mb-3',
                    m('.card-body',
                        m('.card.mb-3',
                            m('.card-body',
                                m('.row.g-2',
                                    m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                                        m('label.form-label[for="userRoleSelect"]', this.getText("LT00000060", "User Role")),
                                        m(SingleSelectComponent, {
                                            id: 'userRoleSelect',
                                            items: userRoleOptionList,
                                            selectedItem: selectedUserRole,
                                            onSelect: (selectedItems) => {
                                                selectedUserRole = selectedItems;
                                                getUserRoleMenuMap();
                                            }
                                        }),
                                    ),
                                    m('.col-xl-3.col-lg-4.col-sm-5.position-relative',
                                        m('label.form-label[for="systemType"]', this.getText("LT00000775", "System Type")),
                                        m('#systemType.btn-group.w-100[role="group"]',
                                            {
                                                onchange: (e) => {
                                                    systemType = parseInt(e.target.value);
                                                    getUserRoleMenuMap();
                                                }
                                            },
                                            m('input#systemTypeOption1.btn-check[type="radio"][name="systemTypeOption"][autocomplete="off"]',
                                                {
                                                    value: SYSTEM_TYPE.WEB,
                                                    checked: systemType === SYSTEM_TYPE.WEB
                                                }
                                            ),
                                            m('label.btn.btn-outline-primary.w-50[for="systemTypeOption1"]', this.getText("LT00000783", "Web")),
                                            m('input#systemTypeOption2.btn-check[type="radio"][name="systemTypeOption"][autocomplete="off"]',
                                                {
                                                    value: SYSTEM_TYPE.MOBILE,
                                                    checked: systemType === SYSTEM_TYPE.MOBILE
                                                }
                                            ),
                                            m('label.btn.btn-outline-primary.w-50[for="systemTypeOption2"]', this.getText("LT00000784", "Mobile")),
                                        )
                                    ),
                                ),
                            )
                        ),
                        m('form.row.g-2',
                            { onsubmit: saveUserRoleMenuByCountry },
                            m('.col-12',
                                m('.table-responsive',
                                    m('table.table.table-hover.sticky-column-start',
                                        m('thead',
                                            m('tr.align-middle',
                                                m('th', this.getText("LT00000025", "Active")),
                                                m('th', this.getText("LT00000778", "Code")),
                                                m('th', this.getText("LT00000785", "Menu")),
                                                m('th', this.getText("LT00000012", "Description")),
                                                m('th', this.getText("LT00000366", "Category")),
                                                m('th', this.getText("LT00000786", "Level")),
                                            )),
                                        m('tbody',
                                            isTableLoading
                                                ? m(Spinner, {
                                                    active: isTableLoading,
                                                    background: true,
                                                    fill: true,
                                                    size: 'xl',
                                                })
                                                : (userRoleMenu.length
                                                    ? m(MenuMapTableComponent, {
                                                        data: userRoleMenu,
                                                        onChange: () => {
                                                            userRoleMenuMapHasChanged = true;
                                                        }
                                                    })
                                                    : m("tr", { "class": "align-middle" },
                                                        m("td", { "colspan": "7" }, languageModel.getText("EM00000141", "No records found."))
                                                    ))
                                        )
                                    )
                                )
                            ),
                            m('.col-12.text-center',
                                m('button.btn.btn-primary[type="submit"]', this.getText("LT00000022", "Save"))
                            )
                        )
                    )
                ),

                this.getBaseViews()
            ];
        }
    };
}
